import { useRouter } from 'next/router';

import { UrlParams } from 'common/url-params';
import { APP_ROUTES } from 'constants/routes';
import { useAnalytics } from 'hooks/useAnalytics';
import { useRouteQueryAnalyticsListProps } from 'hooks/useRouteQueryAnalyticsListProps';

import type {
  IFilter, IFilterOption, IListingActiveFilters, IUrl, PageType
} from 'types';

export function useFilters(params: {pageType: PageType, items: IFilter[] | undefined, multiple?: boolean}) {
  const { pageType, items, multiple = true } = params;

  const analytics = useAnalytics();
  const router = useRouter();
  const { persist: persistAnalyticsListProps } = useRouteQueryAnalyticsListProps();

  const getOptionHref = (option: IFilterOption, parent: IFilter): IUrl => {
    try {
      const { query: { slug: segments = [] } } = router;
      const updatedFilters = getUpdatedFilters(option, parent, items, undefined, multiple);
      const url = UrlParams.setFiltersParams(updatedFilters, { segments, pageType });
      return url;

    } catch (err) {
      console.error('Failed to compute filter URL', err);
    }

    return {
      href: APP_ROUTES.HOME
    };
  };

  const clearFilter = (parent: IFilter) => {
    try {
      const { query: { slug: segments = [] } } = router;

      const mapFn = (filter: IFilter, option: IFilterOption) => (
        filter.code === parent.code
          ? { ...option, isSelected: false }
          : option
      );
      const updatedFilters = getUpdatedFilters(null, parent, items, mapFn);
      const url = UrlParams.setFiltersParams(updatedFilters, { segments, pageType });

      persistAnalyticsListProps();
      analytics.clearFilter(parent?.name);
      router.push(url);

    } catch (err) {
      console.error('Failed to compute filter URL', err);
      router.push(APP_ROUTES.HOME);
    }
  };

  const clearFilters = () => {
    try {
      const { query: { slug: segments } } = router;
      const url = UrlParams.setFiltersParams(null, { segments, pageType });

      persistAnalyticsListProps();
      analytics.clearAllFilters();
      router.push(url);

    } catch (err) {
      console.error('Failed to clear filters', err);
      router.push(APP_ROUTES.HOME);
    }
  };

  return {
    getFilterHref: getOptionHref,
    clearFilter,
    clearFilters
  };
}

function getUpdatedFilters(
  option: IFilterOption | null,
  parent: IFilter,
  items: IFilter[] | undefined,
  mapFn?: (filter: IFilter, option: IFilterOption) => any,
  multiple: boolean = true
): IListingActiveFilters | null {
  if (!items) return null;

  return items.reduce((acc, filter) => {
    const { code, values: options } = filter;

    const defaultMapFn = (o: IFilterOption) => {
      if (code === parent.code && o.code === option?.code) {
        return {
          ...o,
          isSelected: !o?.isSelected
        };
      }

      if (!multiple && code === parent.code) {
        return {
          ...o,
          isSelected: false
        };
      }

      return o;
    };

    const updatedOptions = options
      .map((o) => (
        mapFn
          ? mapFn(filter, o)
          : defaultMapFn(o)
      ))
      .filter((o) => o.isSelected)
      .map((o) => o.code);

    if (updatedOptions.length === 0) return acc;

    return [
      ...(acc || []),
      [code, updatedOptions]
    ];
  }, [] as IListingActiveFilters);
}
